import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

/**
 * Reusable "Pro upgrade" CTA wrapper. Used to gate Findings, Industry KPIs,
 * and the Improvement Ideas section on the report page. The component renders
 * the children as a blurred preview and lays a clear, single-tap unlock CTA
 * on top — so visitors see real content (not a placeholder) which builds
 * trust and conversion.
 *
 * Props:
 *   - children: the section content to gate
 *   - title: small headline on the locked overlay
 *   - subtitle: 1-line explanation of what's behind the gate
 *   - onUnlock: () => void to open the ProUnlockDialog
 *   - testid: optional data-testid for the wrapper
 */
export default function ProSectionGate({
  children,
  title = "Unlock the full breakdown",
  subtitle = "Available with the Pro Pass.",
  onUnlock,
  testid = "pro-section-gate",
}) {
  return (
    <div className="relative" data-testid={testid}>
      {/* Blurred preview of the locked content — provides social proof
          ("real content exists, I'm not staring at a wireframe") while
          preventing reads. */}
      <div
        aria-hidden="true"
        className="select-none pointer-events-none filter blur-md opacity-60"
      >
        {children}
      </div>

      {/* Soft white-to-transparent gradient over the bottom half of the
          preview so the unlock CTA sits on a quiet visual surface. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-b from-transparent via-[#F7F5F0]/85 to-[#F7F5F0] pointer-events-none"
      />

      <div className="absolute inset-0 flex items-end sm:items-center justify-center px-4 sm:px-10 pb-8 sm:pb-0">
        <Card
          className="w-full sm:max-w-lg bg-white border-[#1F382E]/15 shadow-lg"
          data-testid={`${testid}-cta`}
        >
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-start gap-3.5">
              <div className="h-9 w-9 rounded-full bg-[#1F382E]/8 flex items-center justify-center shrink-0">
                <Lock size={15} strokeWidth={1.5} className="text-[#1F382E]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C]">
                  Pro Pass
                </p>
                <p className="font-serif-display text-xl text-[#1F382E] tracking-tight mt-1">
                  {title}
                </p>
                <p className="text-sm text-[#5C665D] leading-relaxed mt-1.5">
                  {subtitle}
                </p>

                <ul className="mt-4 space-y-1.5 text-[13px] text-[#1A1A1A]">
                  <li className="flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C] shrink-0" />
                    Strengths, risks &amp; action items — quantified in your currency
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C] shrink-0" />
                    Industry-specific KPI spotlight
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C] shrink-0" />
                    6–10 prioritized improvement ideas with dollar impact
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C] shrink-0" />
                    Full CFO advisor memo + PDF export
                  </li>
                </ul>

                <Button
                  onClick={onUnlock}
                  className="mt-5 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300 w-full sm:w-auto"
                  data-testid={`${testid}-unlock-btn`}
                >
                  Unlock the full report
                  <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
